import React, { useEffect, useMemo, useState } from "react";
import { DA_ProductItemSlider } from "@organisms/ProductItemSlider";
import {
    PersonalProductRecommendationBuilder,
    RecentlyViewedProductsBuilder,
    PopularProductsBuilder,
    ProductResult,
    UserFactory,
} from "@relewise/client";
import { useRelewise } from "@danishagro/shared/src/contexts/relewise.context";
import { addWebUserGroupsFilter } from "@danishagro/shared/src/helpers/relewiseFilters.helper";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { usePageLocationContext } from "@contexts/PageLocationContext";

export interface V1Props {
    headline: string;
    sliderType: "Latest" | "Popular" | "Personal";
    location?: string;
    alias?: string;
    numberOfProducts?: number;
}

export const V1: React.FC<V1Props> = ({
    headline,
    sliderType,
    location,
    alias,
    numberOfProducts = 6,
}) => {
    const { getRecommender, mapProduct, getDefaultSettings, relewiseUser } = useRelewise();
    const { customerNumber, cvrAndCustomerNumbers } = useAppData();
    const { displayedAtLocation: pageLocation } = usePageLocationContext();

    const [products, setProducts] = useState<ProductResult[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [userInitialized, setUserInitialized] = useState<boolean>(false);

    // Track when user is initialized to prevent duplicate requests with empty user
    // Note: For "Popular" slider, we don't need user tracking (it's aggregate data)
    useEffect(() => {
        // For Popular products, always mark as initialized since they don't need user tracking
        if (sliderType === "Popular") {
            setUserInitialized(true);
            return;
        }

        // For Personal/Latest, check if user has ID
        const user = relewiseUser();
        const userJson = JSON.stringify(user);
        const hasUserId = userJson.includes("temporaryId") || userJson.includes("authenticatedId");
        if (hasUserId) {
            setUserInitialized(true);
        }
    }, [relewiseUser, sliderType]);

    // Clear products when pageLocation changes (during navigation)
    useEffect(() => {
        if (pageLocation) {
            setProducts([]);
            setLoading(true);
        }
    }, [pageLocation]);

    // Memoize settings to prevent unnecessary re-renders
    // Use API-fetched pageLocation instead of globalThis.pageContent
    const settings = useMemo(() => {
        // Wait for location to be loaded
        if (!pageLocation) {
            return null;
        }

        const defaultSettings = getDefaultSettings();

        // Priority: location prop > pageLocation from API > defaultSettings
        const finalLocation = location || pageLocation;

        // If location is manually overridden from Umbraco (location prop exists),
        // use anonymous user to avoid sending user tracking data
        const isManualOverride = !!location;

        return {
            ...defaultSettings,
            displayedAtLocation: finalLocation,
            ...(isManualOverride && {
                user: UserFactory.anonymous(),
            }),
        };
    }, [location, pageLocation, getDefaultSettings]);

    // The number of products is set through Umbraco
    const numberOfProductsShown = numberOfProducts === 0 ? 6 : numberOfProducts;

    useEffect(() => {
        // Don't fetch if settings is null (pageLocation not loaded yet)
        if (!settings) {
            return;
        }

        // For Personal and Latest, wait for user initialization (they need tracking)
        // For Popular, skip user check (it's aggregate data, doesn't need user tracking)
        if ((sliderType === "Personal" || sliderType === "Latest") && !userInitialized) {
            return;
        }

        const fetchData = async () => {
            setLoading(true);

            const recommender = getRecommender();
            if (!recommender) {
                setLoading(false);
                return;
            }

            let builder;
            try {
                switch (sliderType) {
                    case "Popular": {
                        const popularBuilder = new PopularProductsBuilder(settings)
                            .setSelectedProductProperties({
                                displayName: true,
                                pricing: true,
                                allData: true,
                                brand: true,
                                allVariants: true,
                            })
                            .setSelectedVariantProperties({ allData: true })
                            .setNumberOfRecommendations(numberOfProductsShown)
                            .allowFillIfNecessaryToReachNumberOfRecommendations(false)
                            .allowReplacingOfRecentlyShownRecommendations(false)
                            .recommendVariant(true)
                            .basedOn("MostViewed")
                            .sinceMinutesAgo(144000)
                            .prioritizeDiversityBetweenRequests(false);

                        if (globalThis.useCustomerGroups) {
                            popularBuilder.filters((f) =>
                                addWebUserGroupsFilter(f, customerNumber, cvrAndCustomerNumbers)
                            );
                        }
                        builder = popularBuilder.build();
                        const result = await recommender.recommendPopularProducts(builder);

                        setProducts(result?.recommendations ?? []);
                        break;
                    }

                    case "Personal": {
                        if (!globalThis.isAuthenticated) {
                            setLoading(false);
                            return;
                        }

                        const personalBuilder = new PersonalProductRecommendationBuilder(settings)
                            .setSelectedProductProperties({
                                displayName: true,
                                pricing: true,
                                allData: true,
                                brand: true,
                                allVariants: true,
                            })
                            .setSelectedVariantProperties({ allData: true })
                            .setNumberOfRecommendations(numberOfProductsShown)
                            .allowFillIfNecessaryToReachNumberOfRecommendations(false)
                            .allowReplacingOfRecentlyShownRecommendations(false)
                            .recommendVariant(true)
                            .prioritizeDiversityBetweenRequests(false);

                        if (globalThis.useCustomerGroups) {
                            personalBuilder.filters((f) =>
                                addWebUserGroupsFilter(f, customerNumber, cvrAndCustomerNumbers)
                            );
                        }
                        builder = personalBuilder.build();
                        const result = await recommender.recommendPersonalProducts(builder);
                        setProducts(result?.recommendations ?? []);
                        break;
                    }

                    case "Latest": {
                        if (!globalThis.isAuthenticated) {
                            setLoading(false);
                            return;
                        }

                        const recentlyViewedBuilder = new RecentlyViewedProductsBuilder(settings)
                            .setSelectedProductProperties({
                                displayName: true,
                                pricing: true,
                                allData: true,
                                brand: true,
                                allVariants: true,
                            })
                            .setSelectedVariantProperties({ allData: true })
                            .setNumberOfRecommendations(numberOfProductsShown)
                            .allowFillIfNecessaryToReachNumberOfRecommendations(false)
                            .allowReplacingOfRecentlyShownRecommendations(false)
                            .recommendVariant(true)
                            .prioritizeDiversityBetweenRequests(false);

                        if (globalThis.useCustomerGroups) {
                            recentlyViewedBuilder.filters((f) =>
                                addWebUserGroupsFilter(f, customerNumber, cvrAndCustomerNumbers)
                            );
                        }
                        builder = recentlyViewedBuilder.build();
                        const result = await recommender.recentlyViewedProducts(builder);
                        setProducts(result?.recommendations ?? []);
                        break;
                    }

                    default:
                        setLoading(false);
                        return;
                }
            } catch (error) {
                console.error(`API Error (${sliderType}):`, error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [
        sliderType,
        getRecommender,
        settings,
        customerNumber,
        cvrAndCustomerNumbers,
        numberOfProducts,
        numberOfProductsShown,
        userInitialized,
    ]);

    // Display a loading indicator while fetching data
    if (loading) return <div />;

    // Return null if no products exist
    if (!products.length) return null;

    if (alias === "blockProductsHatting") {
        return (
            <DA_ProductItemSlider
                title={headline}
                items={products.map((p) => mapProduct(p))}
                variant="hatting"
            />
        );
    }

    // default:
    return <DA_ProductItemSlider title={headline} items={products.map((p) => mapProduct(p))} />;
};
